import React, { JSX } from 'react';

// Models
import { QuoteProcurementPartInfoResponse } from '@/main/models/response';

// Components
import QuoteProcurementPartInfoColumn from './quoteProcurementPartInfoColumn';
import QuoteProcurementPartInfoRow from './quoteProcurementPartInfoRow';
import {
  getTranslateByKey,
  isEmpty,
  isNullUndefined,
  isNullUndefinedEmpty
} from '@/main/services/utils';
import TooltipComponent from '@/main/components/shared/tooltip/tooltipComponent';
import { PartTypeEnum } from '@/main/services/enum/PartTypeEnum';

const renderTypeColumn = 'column';
const renderTypeRow = 'row';

export function getTooltipFormatted({
  quoteItemDescription,
  quoteItemPartNumber,
  partName,
  partPosition,
  notes
}: QuoteProcurementPartInfoResponse) {
  let quoteItemFormatted: string = '';

  let values = [quoteItemDescription, quoteItemPartNumber];
  quoteItemFormatted = values.filter(value => !isNullUndefinedEmpty(value)).join(' - ');

  let partInfoFormatted: string = '';
  values = [partName, partPosition];
  partInfoFormatted = values.filter(value => !isNullUndefinedEmpty(value)).join(' - ');

  if (partInfoFormatted) {
    if (quoteItemFormatted) quoteItemFormatted += ` (${partInfoFormatted})`;
    else quoteItemFormatted = partInfoFormatted;
  }

  if (notes) quoteItemFormatted += ` [${notes}]`;

  return quoteItemFormatted;
}

export interface RenderPartInformationProps {
  partName?: string;
  partPosition?: string;
  quoteItemDescription?: string;
  partType?: string;
  partTypeQuoteItem?: string;
  quoteSupplierItemPartNumber?: string;
  showPartTypeAndBrandText?: boolean;
  partBrand?: string;
}

export function RenderPartInformation({
  partName,
  partPosition,
  partType,
  partTypeQuoteItem,
  quoteSupplierItemPartNumber
}: RenderPartInformationProps): JSX.Element | null {
  function renderContent(): JSX.Element | null {
    const safePartType = partType === 'undefined' ? undefined : partType;

    const safeQuoteType =
      partTypeQuoteItem === 'undefined' ? undefined : partTypeQuoteItem;
    const hasTypeInfo = !!(safeQuoteType || safePartType);

    let partInfo: JSX.Element = <></>;

    if (safeQuoteType && safePartType) {
      partInfo = (
        <>
          {partInfo}{' '}
          {safePartType === safeQuoteType
            ? `${safePartType}`
            : `${safePartType} (${safeQuoteType})`}{' '}
        </>
      );
    } else if (safeQuoteType) {
      partInfo = (
        <>
          {partInfo} ({safeQuoteType})
        </>
      );
    } else if (safePartType) {
      partInfo = (
        <>
          {partInfo} {safePartType}
        </>
      );
    }

    if (partName) {
      const separator = hasTypeInfo ? ' - ' : '';

      partInfo = (
        <>
          {partInfo}
          {separator}
          {partName}
        </>
      );
      if (partPosition) {
        partInfo = (
          <>
            {partInfo} - {partPosition}
          </>
        );
      }

      if (quoteSupplierItemPartNumber) {
        partInfo = (
          <>
            {partInfo} | {getTranslateByKey('information.sku')}:{' '}
            {quoteSupplierItemPartNumber}
          </>
        );
      }
      return <>{partInfo}</>;
    }

    if (partPosition) {
      if (quoteSupplierItemPartNumber) {
        return (
          <>
            {partPosition} | {getTranslateByKey('information.sku')}:{' '}
            {quoteSupplierItemPartNumber}
          </>
        );
      }
      return <>{partPosition} </>;
    }

    if (quoteSupplierItemPartNumber) {
      partInfo = (
        <>
          {partInfo} | {getTranslateByKey('information.sku')}:{' '}
          {quoteSupplierItemPartNumber}
        </>
      );
    }

    return partInfo;
  }

  return renderContent();
}

export interface RenderQuoteItemInformationProps {
  quoteItemDescription?: string;
  quoteItemPartNumber?: string;
  partType?: string;
  notes?: string;
  tireInfo?: string;
  glassInfo?: string;
  idPartType?: number;
  partName?: string;
}

export function RenderQuoteItemInformation({
  quoteItemDescription,
  quoteItemPartNumber,
  partType,
  notes,
  tireInfo,
  glassInfo,
  idPartType,
  partName
}: RenderQuoteItemInformationProps) {
  const isTireGlass =
    idPartType == PartTypeEnum.Tire || idPartType == PartTypeEnum.Glass;

  function renderGlassTireText() {
    if (!isEmpty(glassInfo)) return `${partType} ${glassInfo}`;
    if (!isEmpty(tireInfo)) return `${partType} ${tireInfo}`;
    return '';
  }

  function renderContent() {
    const parts: React.ReactNode[] = [];

    if (!isNullUndefinedEmpty(quoteItemDescription)) {
      parts.push(
        <span key="desc" style={{ fontWeight: 600 }}>
          {quoteItemDescription}
        </span>
      );
    }

    if (!isNullUndefinedEmpty(quoteItemPartNumber)) {
      if (parts.length > 0) parts.push(<span key="sep"> - </span>);
      parts.push(<span key="partNumber">{quoteItemPartNumber} </span>);
    }

    parts.push(<span key="partNumberSpacer"> </span>);

    let itemInfo: React.ReactNode = <>{parts}</>;

    if (notes)
      itemInfo = (
        <>
          {itemInfo} [{notes}]
        </>
      );

    if (isTireGlass)
      itemInfo = (
        <>
          {itemInfo} {renderGlassTireText()}
        </>
      );

    return <>{itemInfo}</>;
  }

  return renderContent();
}

interface QuoteProcurementPartInfoProps {
  data: QuoteProcurementPartInfoResponse | undefined;
  renderType: 'column' | 'row';
  showLineSupplierInfo?: { info: 'all' | 'partType' | '2lines' };
  showOnlyPartType?: boolean;
  showIconNotes?: boolean;
  rowStyleContainer?: React.CSSProperties;
  isEllipsisText?: boolean;
  isTooltip?: boolean;
  isHyperLink?: boolean;
  onClick?: () => void;
}

function QuoteProcurementPartInfo({
  data,
  renderType,
  showLineSupplierInfo,
  showOnlyPartType,
  rowStyleContainer,
  showIconNotes = false,
  isEllipsisText = true,
  isTooltip = false,
  isHyperLink = false,
  onClick
}: QuoteProcurementPartInfoProps) {
  function renderContent() {
    if (isNullUndefined(data)) {
      return <></>;
    }

    switch (renderType) {
      case renderTypeColumn:
        return (
          <QuoteProcurementPartInfoColumn
            showIconNotes={showIconNotes}
            isTooltip={isTooltip}
            data={data}
            showLineSupplierInfo={showLineSupplierInfo}
            showOnlyPartType={showOnlyPartType}
            isEllipsisText={isEllipsisText}
            isHyperLink={isHyperLink}
            onClick={onClick}
          />
        );

      case renderTypeRow:
        return (
          <QuoteProcurementPartInfoRow
            data={data}
            rowStyleContainer={rowStyleContainer}
            isHyperLink={isHyperLink}
            onClick={onClick}
          />
        );

      default:
        return <></>;
    }
  }

  return renderContent();
}

export default function WrapperQuoteProcurementPartInfo(
  props: QuoteProcurementPartInfoProps
) {
  if (props.isTooltip) {
    return (
      <TooltipComponent
        interactive
        placement="top-start"
        title={
          <QuoteProcurementPartInfo
            isTooltip
            isEllipsisText={false}
            data={props.data}
            renderType="column"
            showLineSupplierInfo={{ info: '2lines' }}
          />
        }>
        <div style={{ minWidth: 0, overflow: 'hidden', width: '100%' }}>
          <QuoteProcurementPartInfo {...props} />
        </div>
      </TooltipComponent>
    );
  }

  return <QuoteProcurementPartInfo {...props} />;
}
